import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { iconForLink } from '../publicPageIcons';
import {
  accentStyle,
  buildHref,
  deriveInitials,
  partitionLinks,
  sortLinks,
  type PageTemplateLink,
  type PageTemplateProps,
} from './shared';

/**
 * Terminal template — monospace, dev/maker vibe. Display name reads as a
 * shell prompt (`> name`), bio renders as a `// comment` block above a
 * grid of bracketed links. Subtle CRT scanline overlay; accent color
 * drives prompt + glow. Looks at home pinned to a developer profile.
 */
export function TerminalTemplate({ page, interactive = true, srcQrId }: PageTemplateProps) {
  const displayName = page.displayName ?? '';
  const bio = page.bio ?? '';
  const theme = page.theme ?? 'dark';
  const accent = page.accentColor || '#22C55E';
  const initials = useMemo(() => deriveInitials(displayName), [displayName]);
  const { primaryVcard, rest: links } = useMemo(
    () => partitionLinks(sortLinks(page.links)),
    [page.links],
  );

  const isDark = theme === 'dark';
  const baseBg = isDark ? '#0A0E0A' : '#F4F2EC';
  const fg = isDark ? '#D2F2D2' : '#0F1A12';
  const dim = isDark ? '#5C6D5C' : '#5C6D5C';
  const linkBoxBorder = isDark ? '#1F2A21' : '#C3D1C0';
  const linkHoverBg = isDark ? '#0F1A12' : '#E9EDDF';

  const minHeightClass = interactive ? 'min-h-screen' : 'min-h-full';

  // CRT-style horizontal scanlines, subtle.
  const scanlines = isDark
    ? 'repeating-linear-gradient(180deg, rgba(255,255,255,0.018) 0px, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 3px)'
    : 'repeating-linear-gradient(180deg, rgba(0,0,0,0.025) 0px, rgba(0,0,0,0.025) 1px, transparent 1px, transparent 3px)';

  return (
    <div
      className={`${minHeightClass} relative w-full font-mono`}
      style={{ ...accentStyle(accent), backgroundColor: baseBg, color: fg }}
      data-theme={theme}
      data-template="terminal"
    >
      {/* Banner first so the scanlines + glow render on top of it like a CRT image. */}
      {page.bannerUrl ? (
        <>
          <div
            className="absolute top-0 left-0 right-0 h-72 sm:h-96 pointer-events-none"
            style={{
              backgroundImage: `url(${page.bannerUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              maskImage: 'linear-gradient(180deg, black 0%, black 55%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(180deg, black 0%, black 55%, transparent 100%)',
            }}
            aria-hidden="true"
          />
          {/* Phosphor tint — pushes the banner toward the accent color so it reads as part of the terminal. */}
          <div
            className="absolute top-0 left-0 right-0 h-72 sm:h-96 pointer-events-none mix-blend-multiply"
            style={{
              background: `linear-gradient(180deg, ${accent}80 0%, ${baseBg} 100%)`,
            }}
            aria-hidden="true"
          />
          {/* Bottom darken so the terminal text below the banner stays legible. */}
          <div
            className="absolute top-0 left-0 right-0 h-72 sm:h-96 pointer-events-none"
            style={{
              background: isDark
                ? 'linear-gradient(180deg, rgba(10,14,10,0) 0%, rgba(10,14,10,0.55) 100%)'
                : 'linear-gradient(180deg, rgba(244,242,236,0) 0%, rgba(244,242,236,0.55) 100%)',
            }}
            aria-hidden="true"
          />
        </>
      ) : null}

      {/* Scanline overlay sits on top so the banner is pixelated/CRT-styled. */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: scanlines }} aria-hidden="true" />

      <div className="relative w-full max-w-[640px] mx-auto px-6 py-14 sm:py-20">
        <div className="flex items-center gap-4 motion-safe:animate-fade-up">
          {page.avatarUrl ? (
            <div
              className="relative w-20 h-20 overflow-hidden"
              style={{
                outline: `2px solid ${accent}`,
                outlineOffset: '2px',
                boxShadow: `0 0 24px ${accent}55`,
              }}
            >
              <img src={page.avatarUrl} alt={`${displayName} avatar`} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div
              className="w-20 h-20 flex items-center justify-center text-2xl"
              style={{
                outline: `2px solid ${accent}`,
                outlineOffset: '2px',
                color: accent,
                boxShadow: `0 0 24px ${accent}55`,
              }}
              aria-hidden="true"
            >
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-widest" style={{ color: dim }}>
              ~/profile
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl break-words">
              <span style={{ color: accent }}>&gt;&nbsp;</span>
              <span>{displayName || 'your_name'}</span>
              <span
                aria-hidden="true"
                className="inline-block w-2 h-5 ml-1 align-middle motion-safe:animate-pulse"
                style={{ backgroundColor: accent }}
              />
            </h1>
          </div>
        </div>

        {bio ? (
          <div
            className="mt-8 font-mono text-sm sm:text-base leading-relaxed motion-safe:animate-fade-up"
            style={{ animationDelay: '60ms' }}
          >
            {bio.split('\n').map((line, i) => (
              <div key={i} className="whitespace-pre-wrap break-words">
                <span style={{ color: accent }}>{'// '}</span>
                <span style={{ color: fg }}>{line || '\u00A0'}</span>
              </div>
            ))}
          </div>
        ) : null}

        {primaryVcard ? (
          <TerminalVcardCta
            link={primaryVcard}
            interactive={interactive}
            srcQrId={srcQrId}
            accent={accent}
            baseBg={baseBg}
            fg={fg}
          />
        ) : null}

        <p
          className="mt-10 text-xs uppercase tracking-widest motion-safe:animate-fade-up"
          style={{ color: dim, animationDelay: '120ms' }}
        >
          $ ls links/
        </p>

        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {links.map((link, i) => {
            const Icon = iconForLink(link.kind, link.icon);
            const label = link.label || 'untitled';
            const commonClass = `group relative flex items-center gap-3 px-4 py-3 border transition-colors duration-150 motion-safe:animate-fade-up`;
            const commonStyle = {
              borderColor: linkBoxBorder,
              animationDelay: `${180 + i * 60}ms`,
            };
            const content = (
              <>
                <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} aria-hidden="true" />
                <Icon className="w-4 h-4 shrink-0" style={{ color: fg }} strokeWidth={1.6} aria-hidden="true" />
                <span className="flex-1 truncate text-sm">
                  <span style={{ color: dim }}>[ </span>
                  {label.toLowerCase()}
                  <span style={{ color: dim }}> ]</span>
                </span>
              </>
            );
            return (
              <li key={link.linkKey}>
                {interactive ? (
                  <a
                    href={buildHref(link.clickHref ?? '', srcQrId)}
                    rel="noopener nofollow"
                    className={`${commonClass} hover:[background-color:var(--hover)]`}
                    style={{ ...commonStyle, ['--hover' as string]: linkHoverBg }}
                  >
                    {content}
                  </a>
                ) : (
                  <div role="link" aria-disabled="true" className={commonClass} style={commonStyle}>
                    {content}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <footer className="mt-14 text-xs" style={{ color: dim }}>
          <a href="/" rel="noopener" className="hover:underline underline-offset-4">
            // made with andmore
          </a>
        </footer>
      </div>
    </div>
  );
}

function TerminalVcardCta({
  link,
  interactive,
  srcQrId,
  accent,
  baseBg,
  fg,
}: {
  link: PageTemplateLink;
  interactive: boolean;
  srcQrId?: string | null;
  accent: string;
  baseBg: string;
  fg: string;
}) {
  const label = (link.label || 'save_contact').toLowerCase().replace(/\s+/g, '_');
  // Block-button styled like a shell command — accent border + glow, fills with
  // accent on hover. Reads like `$ run save_contact.vcf`.
  const className = `group relative flex items-center justify-center gap-3 w-full mt-8 px-5 py-4 transition-colors duration-150 motion-safe:animate-fade-up`;
  const style = {
    border: `1px solid ${accent}`,
    boxShadow: `0 0 24px ${accent}40, inset 0 0 0 1px ${accent}33`,
    backgroundColor: `${accent}12`,
    color: fg,
    animationDelay: '90ms',
  };
  const content = (
    <>
      <span style={{ color: accent }} className="font-mono">$</span>
      <span style={{ color: accent }} className="font-mono">[</span>
      <span className="font-mono text-sm sm:text-base tracking-wide">{label}.vcf</span>
      <span style={{ color: accent }} className="font-mono">]</span>
      <span
        aria-hidden="true"
        className="ml-1 inline-block w-1.5 h-4 motion-safe:animate-pulse"
        style={{ backgroundColor: accent }}
      />
    </>
  );
  return interactive ? (
    <a
      href={buildHref(link.clickHref ?? '', srcQrId)}
      rel="noopener nofollow"
      download="contact.vcf"
      className={`${className} hover:[background-color:var(--accent-strong)] hover:[color:var(--bg-strong)]`}
      style={{ ...style, ['--accent-strong' as string]: accent, ['--bg-strong' as string]: baseBg }}
    >
      {content}
    </a>
  ) : (
    <div role="link" aria-disabled="true" className={className} style={style}>
      {content}
    </div>
  );
}
